/**
 * AZH Telemetry Foundation
 * Lightweight structured event tagging for observability.
 * Transport-agnostic: logs to console in dev, can be wired to
 * a telemetry_events table or OpenTelemetry endpoint later.
 */

const APP_VERSION = "1.0.0"; // sync with package.json at release

function getEnvironment(): string {
  if (typeof window === "undefined") return "server";
  const host = window.location.hostname;
  if (host.includes("localhost") || host.includes("preview")) return "preview";
  return "production";
}

let sessionId: string | null = null;
function getSessionId(): string {
  if (!sessionId) {
    sessionId =
      typeof crypto !== "undefined"
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
  }
  return sessionId;
}

export interface TelemetryEvent {
  event: string;
  level: "info" | "warn" | "error" | "fatal";
  app_version: string;
  environment: string;
  route: string;
  session_id: string;
  timestamp: string;
  organization_id?: string;
  user_id?: string;
  data?: Record<string, unknown>;
}

export function createTelemetryEvent(
  event: string,
  level: TelemetryEvent["level"],
  data?: Record<string, unknown>,
  context?: { organization_id?: string; user_id?: string }
): TelemetryEvent {
  return {
    event,
    level,
    app_version: APP_VERSION,
    environment: getEnvironment(),
    route: typeof window !== "undefined" ? window.location.pathname : "",
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    organization_id: context?.organization_id,
    user_id: context?.user_id,
    data,
  };
}

/**
 * Emit a telemetry event.
 * In production this should POST to an ingestion endpoint or DB table.
 * Currently logs structured JSON for dev/staging visibility.
 */
export function emitTelemetry(
  event: string,
  level: TelemetryEvent["level"] = "info",
  data?: Record<string, unknown>,
  context?: { organization_id?: string; user_id?: string }
): void {
  const payload = createTelemetryEvent(event, level, data, context);

  if (getEnvironment() === "production") {
    // Future: POST to telemetry endpoint or Supabase table
    // For now, structured console for observability tools that scrape logs
    if (level === "error" || level === "fatal") {
      console.error("[telemetry]", JSON.stringify(payload));
    } else {
      // suppressed in prod to avoid noise
    }
  } else {
    // Dev/preview: always log for visibility
    const method = level === "error" || level === "fatal" ? console.error : console.warn;
    method("[telemetry]", payload);
  }
}
